import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { MdAdd, MdGridView, MdList, MdBarChart, MdSearch, MdRefresh, MdAccessTime } from "react-icons/md";
import { useProjects } from "../../../hooks/useProjects";
import { useTeamMembers } from "../../../hooks/useTeamMembers";
import ProjectCard from "./components/ProjectCard";
import CreateProjectModal from "./components/CreateProjectModal";
import ProfitabilityDashboard from "./components/ProfitabilityDashboard";
import LogHoursPanel from "./components/LogHoursPanel";

const STATUSES = ["All", "Ongoing", "Delayed", "Stopped"];

const GLOBAL_CSS = `
@keyframes fadeUp  { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
@keyframes pmPulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.pm-card { animation: fadeUp .2s ease both }
`;

export default function ProjectManagement() {
  const navigate = useNavigate();
  const { projects, loading, fetchAll, createProject } = useProjects();
  const { teamMembers } = useTeamMembers();

  const [view,         setView]         = useState("grid");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search,       setSearch]       = useState("");
  const [showCreate,   setShowCreate]   = useState(false);

  const filtered = useMemo(() => projects.filter(p => {
    if (statusFilter !== "All" && p.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.title?.toLowerCase().includes(q) || p.clientName?.toLowerCase().includes(q);
    }
    return true;
  }), [projects, statusFilter, search]);

  const handleCreate = useCallback(async (data) => {
    try {
      const created = await createProject(data);
      setShowCreate(false);
      // Navigate directly to the new project's detail page
      if (created?.id) navigate(`/admin/projects/${created.id}`);
    } catch (err) { alert("Failed to create project: " + err.message); }
  }, [createProject, navigate]);

  const counts = useMemo(() => ({
    all:     projects.length,
    ongoing: projects.filter(p => p.status === "Ongoing").length,
    delayed: projects.filter(p => p.status === "Delayed").length,
    stopped: projects.filter(p => p.status === "Stopped").length,
  }), [projects]);

  const VIEW_BTNS = [
    { id: "grid",      Icon: MdGridView,   title: "Grid"      },
    { id: "list",      Icon: MdList,       title: "List"      },
    { id: "dashboard", Icon: MdBarChart,   title: "Dashboard" },
    { id: "loghours",  Icon: MdAccessTime, title: "Log Hours" },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif", background: "#f4f4f9", minHeight: "100vh" }}>
      <style>{GLOBAL_CSS}</style>

      {loading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "3px", zIndex: 9999, pointerEvents: "none" }}>
          <div style={{ height: "100%", background: "linear-gradient(90deg,#3761E9,#6c4fff)", animation: "pmPulse 1.2s infinite" }} />
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8f0", padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <div>
            <h1 style={{ fontSize: "20px", fontWeight: 900, color: "#1a1a2e", letterSpacing: "-0.3px" }}>
              Project Management
            </h1>
            <p style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
              {counts.all} projects · {counts.ongoing} ongoing · {counts.delayed} delayed · {counts.stopped} stopped
            </p>
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button onClick={fetchAll} title="Refresh" style={{ width: "36px", height: "36px", border: "1px solid #e0e0ee", borderRadius: "10px", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MdRefresh size={18} color="#666" />
            </button>

            <div style={{ display: "flex", border: "1px solid #e0e0ee", borderRadius: "10px", overflow: "hidden" }}>
              {VIEW_BTNS.map(({ id, Icon, title }, idx) => (
                <button key={id} onClick={() => setView(id)} title={title} style={{
                  width: "36px", height: "36px", border: "none",
                  background: view === id ? "#3761E9" : "#fff",
                  color: view === id ? "#fff" : "#888",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all .15s",
                  borderRight: idx < VIEW_BTNS.length - 1 ? "1px solid #e0e0ee" : "none",
                }}>
                  <Icon size={17} />
                </button>
              ))}
            </div>

            <button onClick={() => setShowCreate(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 18px", height: "36px", border: "none", borderRadius: "10px", background: "#3761E9", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <MdAdd size={18} /> New Project
            </button>
          </div>
        </div>

        {view !== "loghours" && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, maxWidth: "300px" }}>
              <MdSearch size={16} color="#aaa" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects…"
                style={{ width: "100%", padding: "8px 12px 8px 32px", border: "1px solid #e0e0ee", borderRadius: "8px", fontSize: "13px", outline: "none", background: "#fafafa", fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              {STATUSES.map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} style={{
                  padding: "7px 14px", border: "1.5px solid",
                  borderColor: statusFilter === s ? "#3761E9" : "#e0e0ee",
                  borderRadius: "8px", background: statusFilter === s ? "#EEF2FF" : "#fff",
                  color: statusFilter === s ? "#3761E9" : "#666",
                  fontSize: "12px", fontWeight: statusFilter === s ? 700 : 400,
                  cursor: "pointer", fontFamily: "inherit", transition: "all .15s",
                }}>
                  {s} {s !== "All" && <span style={{ opacity: 0.7 }}>({counts[s.toLowerCase()] || 0})</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {view === "loghours" && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MdAccessTime size={16} color="#3761E9" />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#3761E9" }}>Time Tracking — Log your hours across projects</span>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "20px" }}>

        {view === "loghours" && <LogHoursPanel projects={projects} />}

        {view === "dashboard" && <ProfitabilityDashboard projects={projects} />}

        {view === "grid" && (
          filtered.length === 0
            ? (
              <div style={{ textAlign: "center", padding: "80px 0", color: "#bbb" }}>
                <p style={{ fontSize: "48px", marginBottom: "12px" }}>📋</p>
                <p style={{ fontSize: "16px", fontWeight: 600, marginBottom: "6px" }}>No projects found</p>
                <p style={{ fontSize: "13px" }}>
                  {projects.length === 0 ? "Projects auto-create when a CRM deal is marked 'Closed Won'" : "Try adjusting your filters"}
                </p>
                {projects.length === 0 && (
                  <button onClick={() => setShowCreate(true)} style={{ marginTop: "16px", padding: "10px 24px", border: "none", borderRadius: "10px", background: "#3761E9", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    Create Manually
                  </button>
                )}
              </div>
            )
            : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "14px" }}>
                {filtered.map((p, i) => (
                  <div key={p.id} className="pm-card" style={{ animationDelay: `${i * 0.04}s` }}>
                    {/* Navigate to full-page detail on click */}
                    <ProjectCard project={p} onClick={() => navigate(`/admin/projects/${p.id}`)} />
                  </div>
                ))}
              </div>
            )
        )}

        {view === "list" && (
          <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e8e8f0", overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1fr 1fr", gap: "8px", padding: "10px 16px", background: "#f7f8ff", borderBottom: "1px solid #e8e8f0" }}>
              {["Project", "Client", "Status", "Contract", "Profit", "Delivery"].map(h => (
                <p key={h} style={{ fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</p>
              ))}
            </div>
            {filtered.length === 0
              ? <p style={{ textAlign: "center", padding: "40px", color: "#bbb", fontSize: "13px" }}>No projects found</p>
              : filtered.map((p, i) => {
                const profitColor = (p.forecastedProfit || 0) >= 0 ? "#2E7D32" : "#B71C1C";
                const statusCfg = { Ongoing: ["#E8F5E9","#2E7D32"], Delayed: ["#FFF8E1","#E65100"], Stopped: ["#FFEBEE","#B71C1C"] };
                const [sBg, sText] = statusCfg[p.status] || statusCfg.Ongoing;
                return (
                  <div key={p.id}
                    onClick={() => navigate(`/admin/projects/${p.id}`)}
                    style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1fr 1fr", gap: "8px", padding: "12px 16px", borderBottom: i < filtered.length - 1 ? "1px solid #f0f0f8" : "none", cursor: "pointer", transition: "background .15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f9f9ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                  >
                    <div style={{ overflow: "hidden" }}>
                      <p style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</p>
                      {p.referrerName && <p style={{ fontSize: "10px", color: "#E65100" }}>🤝 {p.referrerName}</p>}
                    </div>
                    <p style={{ fontSize: "12px", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", alignSelf: "center" }}>{p.clientName}</p>
                    <div style={{ alignSelf: "center" }}>
                      <span style={{ background: sBg, color: sText, borderRadius: "999px", padding: "3px 8px", fontSize: "10px", fontWeight: 700 }}>{p.status}</span>
                    </div>
                    <p style={{ fontSize: "12px", fontWeight: 700, color: "#3761E9", alignSelf: "center" }}>
                      {new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(p.totalContractValue)}
                    </p>
                    <p style={{ fontSize: "12px", fontWeight: 700, color: profitColor, alignSelf: "center" }}>
                      {new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(p.forecastedProfit)}
                    </p>
                    <p style={{ fontSize: "12px", color: "#666", alignSelf: "center" }}>{p.deliveryDate || "—"}</p>
                  </div>
                );
              })
            }
          </div>
        )}
      </div>

      {/* Create Modal Portal */}
      {showCreate && createPortal(
        <CreateProjectModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
          teamMembers={teamMembers}
        />,
        document.body
      )}
    </div>
  );
}
